import { Injectable, signal } from '@angular/core';

export type CookieConsent = 'all' | 'declined' | 'custom';

export interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'lumio_cookie_consent';
const PREFS_KEY = 'lumio_cookie_preferences';

const DEFAULT_PREFS: CookiePreferences = { analytics: true, marketing: true };

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly _consent = signal<CookieConsent | null>(this.load());

  /** True when the user has not yet made a consent decision. */
  readonly needsConsent = signal(this._consent() === null);

  getConsent(): CookieConsent | null {
    return this._consent();
  }

  getPreferences(): CookiePreferences {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_PREFS };
    try {
      const stored = localStorage.getItem(PREFS_KEY);
      return stored ? (JSON.parse(stored) as CookiePreferences) : { ...DEFAULT_PREFS };
    } catch {
      return { ...DEFAULT_PREFS };
    }
  }

  acceptAll(): void {
    this.savePreferences({ analytics: true, marketing: true });
    this.save('all');
  }

  declineAll(): void {
    this.savePreferences({ analytics: false, marketing: false });
    this.save('declined');
  }

  saveCustom(prefs: CookiePreferences): void {
    this.savePreferences(prefs);
    this.save('custom');
  }

  private save(value: CookieConsent): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, value);
    }
    this._consent.set(value);
    this.needsConsent.set(false);
  }

  private savePreferences(prefs: CookiePreferences): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    }
  }

  private load(): CookieConsent | null {
    if (typeof localStorage === 'undefined') return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as CookieConsent) ?? null;
  }
}
